import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { getIn } from 'formik';
import uniq from 'lodash/uniq';
import { Select, SelectOption, SelectGroupTitle } from '@pwc/appkit-react/lib/Select';

import ErrorMessage from './errorMessage.component';
import AppkitIcon from '../../appkitIcon/appkitIcon.component';
import { SelectSizes, SelectTabKinds, SelectToggleModes } from '../constants';
import { SelectValuePropTypes, SelectOptionPropTypes, SelectTabPropTypes, FormikPropTypes } from '../propTypes';

class SelectFormik extends React.PureComponent {
  static propTypes = {
    label: PropTypes.node,
    name: PropTypes.string.isRequired,
    onSelect: PropTypes.func,
    options: PropTypes.arrayOf(SelectOptionPropTypes).isRequired,
    placeholder: PropTypes.string,
    size: PropTypes.oneOf(Object.values(SelectSizes)),
    disabled: PropTypes.bool,
    defaultValue: SelectValuePropTypes,
    value: SelectValuePropTypes,
    multiple: PropTypes.bool,
    showSelectAll: PropTypes.bool,
    showSearchOnToggle: PropTypes.bool,
    showSearchOnList: PropTypes.bool,
    showTagsOnToggle: PropTypes.bool,
    tabs: PropTypes.arrayOf(SelectTabPropTypes),
    tabsKind: PropTypes.oneOf(Object.values(SelectTabKinds)),
    toggleMode: PropTypes.oneOf(Object.values(SelectToggleModes)),
    formik: FormikPropTypes.isRequired,
    clearable: PropTypes.bool,
    grouped: PropTypes.bool,
    handleExternalChange: PropTypes.bool,
    hidden: PropTypes.bool,
  };

  static defaultProps = {
    placeholder: 'Select...',
    size: SelectSizes.DEFAULT,
    disabled: false,
    multiple: false,
    showSelectAll: false,
    showSearchOnToggle: false,
    showSearchOnList: false,
    showTagsOnToggle: false,
    tabsKind: SelectTabKinds.PRIMARY,
    toggleIconKind: SelectToggleModes.SELECT,
    clearable: false,
    grouped: false,
    hidden: false,
    handleExternalChange: false,
  };

  componentDidUpdate(prevProps) {
    const { handleExternalChange, value, name } = this.props;
    const { value: prevValue, name: prevName } = prevProps;

    if (handleExternalChange && (value !== prevValue || name !== prevName)) {
      this.handleSelect(value);
    }
  }

  handleSelect = value => {
    const { name, onSelect, formik } = this.props;
    formik.setTouched({
      ...formik.touched,
      [name]: true,
    });
    formik.setFieldValue(name, value);

    if (onSelect) {
      onSelect(value, { formik });
    }
  };

  getGroupedOptions = groupedOptions => uniq(groupedOptions.map(option => option.group))
    .reduce((acc, group) => [
      ...acc,
      <SelectGroupTitle key={group}>{group}</SelectGroupTitle>,
      ...groupedOptions.filter(option => option.group === group)
        .map(({ label, value }) => <SelectOption key={value} value={value}>{label}</SelectOption>),
    ], []);

  getOptions = options => options.map(({ label, value }) =>
    <SelectOption key={value} value={value} >{label}</SelectOption>);

  clearSelection = () => {
    const { name, formik: { setFieldValue } } = this.props;
    setFieldValue(name, null);
  }; // eslint-disable-line react-hooks/exhaustive-deps

  render() {
    const {
      label,
      name,
      options,
      placeholder,
      size,
      disabled,
      defaultValue,
      value,
      multiple,
      showSelectAll,
      showSearchOnToggle,
      showSearchOnList,
      showTagsOnToggle,
      tabs,
      tabsKind,
      toggleMode,
      formik,
      clearable,
      grouped,
      hidden,
    } = this.props;

    if (hidden) {
      return null;
    }

    const error = getIn(formik.errors, name);
    const touched = getIn(formik.touched, name);
    const shouldUseErrorStyles = touched && error;
    // PG-338 temporary solution, waiting for appkit team to introduce clearable prop
    const showClearIcon = clearable && Boolean(value);

    const optionsComponents = grouped ? this.getGroupedOptions(options) : this.getOptions(options);

    return (
      <>
        {label && <label className="a-form-label">{label}</label>}
        <div className="appkit-select-custom-wrapper">
          <Select
            key={name}
            name={name}
            className={classnames('appkit-select-custom', { 'with-error': shouldUseErrorStyles })}
            onSelect={this.handleSelect}
            placeholder={placeholder}
            size={size}
            disabled={disabled}
            defaultValue={defaultValue}
            value={value}
            multiple={multiple}
            showSelectAll={showSelectAll}
            showSearchOnToggle={showSearchOnToggle}
            showSearchOnList={showSearchOnList}
            showTagsOnToggle={showTagsOnToggle}
            tabs={tabs}
            tabsKind={tabsKind}
            toggleMode={toggleMode}
          >
            {optionsComponents}
          </Select>
          {
            showClearIcon && (
              <AppkitIcon
                className="clear-icon"
                icon="close"
                type="fill"
                size={8}
                onClick={this.clearSelection}
              />
            )
          }
        </div>
        <div className="invalid-feedback">
          <ErrorMessage name={name} formik={formik} />
        </div>
      </>
    );
  }
}

export default SelectFormik;
